import { type JSX, useEffect } from 'react';
import { useIntl } from 'react-intl';
import NullStateAnalytic from '@alltrails/amplitude/enums/NullState';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import NullState from '@alltrails/denali/components/NullState';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Binoculars from '@alltrails/denali/illustrations/Binoculars';
import { useAppDispatch } from '../../../redux';
import { clearFilters, clearQuery } from '../../../slices/reviewsSearchFilters';
import styles from './NoResults.module.scss';

type NoResultsProps = {
  hasFilters: boolean;
  hasSearch: boolean;
  onClear: (action: NullStateAction, state: NullStateAnalytic) => void;
  onViewed?: (state: NullStateAnalytic) => void;
};

const NoResults = ({ hasFilters, hasSearch, onClear, onViewed }: NoResultsProps): JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();

  const clearSearchFilters = () => {
    if (hasSearch) {
      dispatch(clearQuery());
    } else if (hasFilters) {
      dispatch(clearFilters());
    }
    onClear(hasSearch ? 'clear search' : 'clear filter', 'no content');
  };

  useEffect(() => {
    onViewed?.('no content');
    // only fire when the component mounts
  }, []);

  if (!hasSearch && !hasFilters) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <NullState
        buttons={{
          [isMobile ? 'primary' : 'default']: {
            onClick: clearSearchFilters,
            testId: `clear-reviews-${hasSearch ? 'search' : 'filters'}`,
            text: hasSearch ? intl.formatMessage({ defaultMessage: 'Clear search' }) : intl.formatMessage({ defaultMessage: 'Clear filters' })
          }
        }}
        className={styles.error}
        description={intl.formatMessage({ defaultMessage: 'Let’s get you back on track! Try a new search or adjust your filters.' })}
        illustration={{ Component: Binoculars }}
        noTopMargin
        title={intl.formatMessage({ defaultMessage: 'No results found' })}
      />
    </div>
  );
};

export default NoResults;
